import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Alert, Card, Chip } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { FinancialPositionReadiness } from '@/api/models';
import { READINESS_STEPS, ZONES } from '../../financialPositionMeta';
import { ZoneTrend } from './ZoneTrend';

type ZoneRailViewProps = {
    /** Which readiness steps the ledger actually satisfies. */
    readiness: FinancialPositionReadiness;
};

/** Semantic text colour per zone — the dot and the trend arrow read it. */
const ZONE_TONE = {
    strained: 'text-danger',
    stable: 'text-warning',
    strong: 'text-success',
} as const;

/**
 * The assessment rail while there is nothing to assess.
 *
 * All three zones stay on screen, greyed, and a fourth accent panel takes the
 * slot the live zone would occupy. Keeping the sequence visible is the point:
 * it reads as *pending* rather than broken, and it tells a company on its first
 * day what this screen will eventually say about it. The readiness chips double
 * as the checklist that gets it there.
 */
export const ZoneRailView: React.FC<ZoneRailViewProps> = ({ readiness }) => {
    const { t } = useTranslation('pages');

    return (
        <Card {...getNodeTestIdProp('financial-position-zone-rail')}>
            <Card.Content className="grid gap-3 lg:grid-cols-2">
                <div className="grid gap-3 sm:grid-cols-3">
                    {ZONES.map((zone) => (
                        <Card
                            key={zone.id}
                            variant="secondary"
                            {...getNodeTestIdProp(
                                `financial-position-zone-${zone.id}`,
                            )}
                        >
                            <Card.Header>
                                <Card.Title className="flex items-center gap-2">
                                    {/* A plain filled dot, as in every other
                                        zone legend: a ringed icon at this size
                                        reads as a control, not a status. */}
                                    <Icon
                                        aria-hidden
                                        className={ZONE_TONE[zone.id]}
                                        icon="gravity-ui:circle-fill"
                                        width={8}
                                    />
                                    {t(zone.labelKey)}
                                </Card.Title>
                                <Card.Description>
                                    {t(zone.captionKey)}
                                </Card.Description>
                            </Card.Header>
                            <Card.Content
                                className={`flex justify-center ${ZONE_TONE[zone.id]}`}
                            >
                                <ZoneTrend
                                    ariaLabel={t(
                                        `analytics.financialPosition.trends.${zone.trend}`,
                                    )}
                                    trend={zone.trend}
                                />
                            </Card.Content>
                        </Card>
                    ))}
                </div>
                {/* Outlined, unlike every other Alert in the app. This one
                    is not a message laid over a screen — it *is* the fourth
                    tile in the rail, standing where the live zone will stand,
                    so it needs the same bounded edge the three ghosts have.
                    Border only, no fill: `accent-soft` is the same light blue
                    in both themes, and a dark-mode panel of light blue behind
                    light text is unreadable. */}
                <Alert
                    className="rounded-xl border border-accent"
                    status="accent"
                    {...getNodeTestIdProp('financial-position-pending')}
                >
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Title>
                            {t('analytics.financialPosition.pending.title')}
                        </Alert.Title>
                        <Alert.Description>
                            {t('analytics.financialPosition.pending.body')}
                        </Alert.Description>
                        <div className="flex flex-wrap gap-2 pt-1">
                            {READINESS_STEPS.map((step) => (
                                <Chip
                                    color={
                                        readiness[step.id].met
                                            ? 'success'
                                            : 'default'
                                    }
                                    key={step.id}
                                    {...getNodeTestIdProp(
                                        `financial-position-readiness-${step.id}`,
                                    )}
                                >
                                    <Icon
                                        aria-hidden
                                        icon={
                                            readiness[step.id].met
                                                ? 'solar:check-circle-bold'
                                                : 'solar:clock-circle-linear'
                                        }
                                    />
                                    <Chip.Label>
                                        {t(step.labelKey, {
                                            configured:
                                                readiness.expenses.configured,
                                            required:
                                                readiness.expenses.required,
                                        })}
                                    </Chip.Label>
                                </Chip>
                            ))}
                        </div>
                    </Alert.Content>
                </Alert>
            </Card.Content>
        </Card>
    );
};
